/**
 * Handles actions related to settings that utilise the Rich Text Editor (Quill)
 *
 * @package Ninja Forms builder
 * @subpackage App - Settings Drawer
 * @copyright (c) 2015 WP Ninjas
 * @since 3.0
 */
define( 'controllers/app/rte',[], function() {
	var controller = Marionette.Object.extend( {
		initialize: function() {
			// We don't want the RTE setting to re-render when the value changes.
			nfRadio.channel( 'setting-type-rte' ).reply( 'renderOnChange', function(){ return false } );

			this.listenTo( nfRadio.channel( 'rte' ), 'init:settingModel', this.initSettingModel );

			// When an RTE setting is shown, re-render RTE.
			this.listenTo( nfRadio.channel( 'setting-type-rte' ), 'render:setting', this.renderSetting );

			// When an RTE setting view is destroyed, remove our RTE.
			this.listenTo( nfRadio.channel( 'setting-type-rte' ), 'destroy:setting', this.destroySetting );

			// When an element within the RTE is clicked, check to see if we should insert a link.
			this.listenTo( nfRadio.channel( 'setting-type-rte' ), 'click:extra', this.clickExtra );

			// Instantiates the variable that holds the media library frame.
			this.meta_image_frame;

			this.currentContext = {};
			this.quillInstances = {}; // Track Quill instances

			// Register style attributors so Quill preserves inline styles (margin, padding, etc.)
			// This must be done before any Quill instances are created.
			this.registerStyleAttributors();
		},

		/**
		 * Register Parchment StyleAttributors for CSS properties that Quill doesn't
		 * natively support. This allows inline styles like margin and padding to
		 * survive the Delta conversion and remain editable.
		 *
		 * Without this, Quill's Delta architecture discards any style attribute
		 * it doesn't have a registered Attributor for.
		 */
		registerStyleAttributors: function() {
			var that = this;

			// Wait for Quill to be available
			if (typeof Quill === 'undefined') {
				setTimeout(function() { that.registerStyleAttributors(); }, 100);
				return;
			}

			var Parchment = Quill.import('parchment');
			if (!Parchment || Parchment._nfStyleAttributorsRegistered) {
				return; // Already registered or Parchment not available
			}

			// CSS properties to preserve through Quill's Delta conversion
			var styleProps = [
				// Margin properties
				'margin', 'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
				// Padding properties
				'padding', 'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
				// Other common styling properties
				'line-height', 'text-indent'
			];

			styleProps.forEach(function(prop) {
				// Create a StyleAttributor for each property
				// Scope.BLOCK means it applies to block-level elements (p, h1, etc.)
				var attributor = new Parchment.StyleAttributor(prop, prop, {
					scope: Parchment.Scope.BLOCK
				});
				Quill.register(attributor, true);
			});

			// Also register inline-level style attributors for spans and inline elements
			var inlineStyleProps = [
				'color', 'background-color', 'font-size', 'font-family'
			];

			inlineStyleProps.forEach(function(prop) {
				var attributor = new Parchment.StyleAttributor(prop, prop, {
					scope: Parchment.Scope.INLINE
				});
				Quill.register(attributor, true);
			});

			// Register text-align as a StyleAttributor so alignment outputs inline styles
			// instead of Quill's default class-based alignment (ql-align-center, etc.)
			// Fixes issue #7923
			var textAlignAttributor = new Parchment.StyleAttributor('align', 'text-align', {
				scope: Parchment.Scope.BLOCK,
				whitelist: ['', 'center', 'right', 'justify']
			});
			Quill.register(textAlignAttributor, true);

			Parchment._nfStyleAttributorsRegistered = true;

			// Register a custom Div blot so <div> elements are preserved
			// instead of being converted to <p> elements
			var Block = Quill.import('blots/block');

			// Create DivBlot extending Block
			class DivBlot extends Block {
				static create(value) {
					var node = super.create();
					return node;
				}
			}
			DivBlot.blotName = 'div';
			DivBlot.tagName = 'DIV';

			Quill.register(DivBlot, true);

			// Register BlotFormatter for image resize handles (#7879)
			if (typeof QuillBlotFormatter !== 'undefined' && QuillBlotFormatter.default) {
				Quill.register('modules/blotFormatter', QuillBlotFormatter.default);
			}

			// =====================================================================
			// Custom List Blots - Fix for #7937, #7938, #7942
			// =====================================================================
			// Quill's default ListItem injects <span class="ql-ui"> elements for
			// checkbox toggle UI. These spans persist in saved HTML and trigger
			// the code-view lock on subsequent edits.
			//
			// Our custom implementation:
			// 1. NfListItem - ListItem without UI span injection
			// 2. NfOrderedList - Container for <ol> elements
			// 3. NfBulletList - Container for <ul> elements (Quill only supports OL by default)
			// =====================================================================

			var Container = Quill.import('blots/container');
			var Scroll = Quill.import('blots/scroll');

			// Custom ListItem that does NOT inject ql-ui spans
			// The key difference from Quill's ListItem: no constructor override = no attachUI()
			class NfListItem extends Block {
				static create(value) {
					var node = super.create();
					node.setAttribute('data-list', value);
					return node;
				}

				static formats(domNode) {
					return domNode.getAttribute('data-list') || undefined;
				}

				format(name, value) {
					if (name === this.statics.blotName && value) {
						this.domNode.setAttribute('data-list', value);
					} else {
						super.format(name, value);
					}
				}
			}
			NfListItem.blotName = 'list';
			NfListItem.tagName = 'LI';

			// Container for ordered lists
			class NfOrderedList extends Container {
				static create(value) {
					return super.create();
				}
			}
			NfOrderedList.blotName = 'ordered-list';
			NfOrderedList.tagName = 'OL';

			// Container for unordered lists (Quill doesn't support UL by default)
			class NfBulletList extends Container {
				static create(value) {
					return super.create();
				}
			}
			NfBulletList.blotName = 'bullet-list';
			NfBulletList.tagName = 'UL';

			// Set up container relationships
			NfOrderedList.allowedChildren = [NfListItem];
			NfBulletList.allowedChildren = [NfListItem];

			// ListItem can be in either container type
			NfListItem.requiredContainer = NfOrderedList;

			// Register with Quill (true = override existing)
			Quill.register(NfListItem, true);
			Quill.register(NfOrderedList, true);
			Quill.register(NfBulletList, true);

			// Update Scroll's allowed children to include our list containers
			var ScrollBlot = Quill.import('blots/scroll');
			if (ScrollBlot && ScrollBlot.allowedChildren) {
				if (ScrollBlot.allowedChildren.indexOf(NfOrderedList) === -1) {
					ScrollBlot.allowedChildren.push(NfOrderedList);
				}
				if (ScrollBlot.allowedChildren.indexOf(NfBulletList) === -1) {
					ScrollBlot.allowedChildren.push(NfBulletList);
				}
			}

			// =====================================================================
			// Custom Span Blot - Fix for #7942
			// =====================================================================
			// Quill strips custom id and class attributes from spans. This blot
			// preserves them through the Delta conversion.
			// =====================================================================

			var Inline = Quill.import('blots/inline');

			class NfSpan extends Inline {
				static create(value) {
					var node = super.create();
					if (value && typeof value === 'object') {
						if (value.id) node.setAttribute('id', value.id);
						if (value.class) node.setAttribute('class', value.class);
					}
					return node;
				}

				static formats(domNode) {
					var formats = {};
					if (domNode.hasAttribute('id')) {
						formats.id = domNode.getAttribute('id');
					}
					if (domNode.hasAttribute('class')) {
						// Filter out Quill's internal classes
						var classes = domNode.getAttribute('class');
						var filtered = classes.split(' ').filter(function(c) {
							return c && !c.startsWith('ql-');
						}).join(' ');
						if (filtered) {
							formats.class = filtered;
						}
					}
					return Object.keys(formats).length > 0 ? formats : undefined;
				}

				format(name, value) {
					if (name === 'nf-span') {
						if (value && typeof value === 'object') {
							if (value.id) this.domNode.setAttribute('id', value.id);
							if (value.class) this.domNode.setAttribute('class', value.class);
						}
					} else {
						super.format(name, value);
					}
				}
			}
			NfSpan.blotName = 'nf-span';
			NfSpan.tagName = 'SPAN';

			Quill.register(NfSpan, true);

			// Monkey-patch TableRow.checkMerge to prevent infinite loop on malformed HTML (#7944)
			// When malformed table HTML is pasted (e.g., orphan <tr> elements, whitespace between
			// table elements), Quill can create TableRow blots with non-TableCell children.
			// The original checkMerge() assumes all children have a formats() method, causing
			// TypeError which doesn't halt the optimize cycle, resulting in infinite loop.
			this.patchTableRowCheckMerge();
		},

		/**
		 * Patch TableRow.checkMerge() to add defensive type checking.
		 * Prevents infinite loop when malformed table HTML creates invalid blot structure.
		 * Fixes issue #7944.
		 */
		patchTableRowCheckMerge: function() {
			var TableRow = Quill.import('formats/table-row');
			if (!TableRow || TableRow._nfCheckMergePatched) {
				return;
			}

			var originalCheckMerge = TableRow.prototype.checkMerge;

			TableRow.prototype.checkMerge = function() {
				// Defensive check: ensure all children have formats() method (are TableCells)
				// If any child is not a proper TableCell, skip the merge to prevent TypeError
				if (!this.children || !this.children.head) {
					return false;
				}
				if (typeof this.children.head.formats !== 'function') {
					return false;
				}
				if (!this.children.tail || typeof this.children.tail.formats !== 'function') {
					return false;
				}
				if (this.next && this.next.children) {
					if (this.next.children.head && typeof this.next.children.head.formats !== 'function') {
						return false;
					}
					if (this.next.children.tail && typeof this.next.children.tail.formats !== 'function') {
						return false;
					}
				}
				return originalCheckMerge.call(this);
			};

			TableRow._nfCheckMergePatched = true;
		},

		initSettingModel: function( settingModel ) {
			// Hide the standard merge tag button - we use the one in the Quill toolbar instead
			settingModel.set( 'hide_merge_tags', true );
		},

		initRTE: function( settingModel, dataModel, settingView ) {
			var that = this;
			var settingEl = jQuery( settingView.el ).find( 'div.setting' );

			// Check if already initialized to prevent infinite loop
			if (settingEl.data('quill-initialized')) {
				return;
			}

			// Mark as initialized
			settingEl.data('quill-initialized', true);

			// Clear any existing content and create a container for Quill
			settingEl.empty();

			// Create unique ID for this instance
			var instanceId = 'quill-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
			var quillContainer = jQuery('<div id="' + instanceId + '"></div>');
			settingEl.append(quillContainer);

			// Use requestAnimationFrame for better timing
			requestAnimationFrame(function() {
				// Double-check element is still in DOM and attached
				if (jQuery.contains(document, quillContainer[0])) {
					that.initQuillInstance(instanceId, settingModel, dataModel, settingView);
				}
			});
		},

		initQuillInstance: function( instanceId, settingModel, dataModel, settingView ) {
			var that = this;
			var settingEl = jQuery( settingView.el ).find( 'div.setting' );

			// Verify element exists and is in the DOM
			var containerEl = document.getElementById(instanceId);
			if (!containerEl || !jQuery.contains(document, containerEl)) {
				return; // Silently fail if element not found
			}

			// Define custom toolbar
			var toolbarOptions = [
				[{ 'header': [1, 2, 3, false] }],
				['bold', 'italic', 'underline', 'clean'],
				[{ 'list': 'ordered'}, { 'list': 'bullet' }],
				[{ 'align': ['', 'center', 'right', 'justify'] }],
				['nf-link', 'nf-unlink'],
				[{ 'table': 'table' }],
				['nf-undo', 'nf-redo'],
				['nf-media', 'nf-code', 'nf-merge-tags']
			];

			// Build modules config
			var modulesConfig = {
				toolbar: {
					container: toolbarOptions,
					handlers: {
						'nf-link': function() {
							that.showLinkDialog(quill, settingView);
						},
						'nf-unlink': function() {
							that.removeLink(quill);
						},
						'nf-media': function() {
							that.openMediaManager(quill, settingView);
						},
						'nf-merge-tags': function() {
							that.showMergeTags(quill, settingView);
						},
						'nf-code': function() {
							that.toggleCodeView(quill, settingView, settingModel, dataModel);
						},
						'nf-undo': function() {
							quill.history.undo();
						},
						'nf-redo': function() {
							quill.history.redo();
						},
						'table': function() {
							that.showTablePicker(quill, settingView);
						}
					}
				},
				history: {
					delay: 1000,
					maxStack: 50,
					userOnly: true
				},
				table: true,
				blotFormatter: {}  // Image resize handles (#7879)
			};

			// Initialize Quill
			var quill = new Quill('#' + instanceId, {
				theme: 'snow',
				modules: modulesConfig,
				placeholder: ''
			});

			// Add clipboard matcher to sanitize table HTML during paste operations (#7944)
			// This runs before Quill's Delta conversion, fixing malformed table structures
			// that would otherwise cause infinite loops in checkMerge()
			quill.clipboard.addMatcher('TABLE', function(node, delta) {
				// Create a temporary container and re-parse the table through our sanitizer
				var wrapper = document.createElement('div');
				wrapper.appendChild(node.cloneNode(true));
				var sanitized = that.normalizeTableHtml(wrapper.innerHTML);

				// If sanitization changed the structure, re-convert
				if (sanitized !== wrapper.innerHTML) {
					var tempContainer = document.createElement('div');
					tempContainer.innerHTML = sanitized;
					// Use Quill's clipboard to convert the sanitized HTML
					return quill.clipboard.convert({ html: sanitized });
				}

				return delta;
			});

			// Set initial content — normalize legacy Summernote tables for Quill compatibility
			var initialValue = dataModel.get( settingModel.get( 'name' ) ) || '';
			var hasLegacyTable = this.hasLegacyTables(initialValue);

			// Store tracking state on the Quill instance BEFORE assigning innerHTML.
			// Setting innerHTML triggers Quill's MutationObserver which emits a
			// text-change event with source 'user'. Without these flags being set
			// first, both text-change handlers below (persistence + merge-tag
			// detection) would react to the initial content load — which causes
			// the merge-tag box to auto-appear when the loaded content contains
			// closed merge tags like {calc:calc1} (#7884).
			quill._nfOriginalHtml = initialValue;
			quill._nfUserHasEdited = false;
			quill._nfSkipNextTextChange = true;
			quill._nfSkipNextMergeTagDetection = true;

			// Pre-process HTML: normalize tables and prepare lists for Quill
			var processedHtml = this.normalizeTableHtml(initialValue);
			processedHtml = this.prepareHtmlForQuill(processedHtml);
			quill.root.innerHTML = processedHtml;

			if (hasLegacyTable) {
				this.showLegacyTableNotice(settingView, instanceId, quill, settingModel, dataModel);
			}

			// Store instance
			this.quillInstances[instanceId] = {
				quill: quill,
				settingModel: settingModel,
				dataModel: dataModel,
				settingView: settingView
			};

			// Store Quill reference on the editor element for external access
			quill.root.__quill = quill;

			// Also store on the setting element for merge tag button access
			settingEl.data('quill', quill);

			// Add custom button icons using Dashicons
			this.addDashicons(settingView, settingModel);

			// Manually attach click handler to merge tags button
			// We can't rely on mergeTagBox's automatic attachment since timing is wrong
			setTimeout(function() {
				var toolbar = jQuery(settingView.el).find('.ql-toolbar');
				var mergeButton = toolbar.find('.merge-tags');

				// Remove any existing handlers and attach new one
				mergeButton.off('click').on('click', function(e) {
					var quillInstance = that.quillInstances[instanceId];
					if (!quillInstance) return;

					var quill = quillInstance.quill;
					var settingContainer = jQuery(settingView.el).find('.setting');

					// When in code view, insert into CodeMirror instead of the hidden Quill editor
					if (settingContainer.hasClass('code-view')) {
						var cm = settingContainer.data('codeMirror');
						if (cm) {
							var cursor = cm.getCursor();
							cm.replaceRange('{', cursor);
							// Position caret after the inserted '{'
							var newPos = { line: cursor.line, ch: cursor.ch + 1 };
							cm.setCursor(newPos);
							cm.focus();

							// Set merge tag system variables
							nfRadio.channel('mergeTags').request('set:old', '{');
							// Use a flat character offset for the merge tag system
							var charOffset = cm.indexFromPos(newPos);
							nfRadio.channel('mergeTags').request('set:caret', charOffset);

							// Store a CodeMirror-aware context for merge tag insertion
							window.nfCurrentQuillInstance = null;
							window.nfCurrentCodeMirror = cm;

							// Update tag list
							nfRadio.channel('merge-tags').request('update:taglist', 'fields', false);

							// Show the merge tags box
							jQuery('#merge-tags-box').css('display', 'block');

							// Position it near the CodeMirror editor
							nfRadio.channel('mergeTags').trigger('focus', cm.getWrapperElement(), 'rte');

							// Focus the search input
							setTimeout(function() {
								jQuery('#merge-tags-box').find('.merge-tag-filter').find('input').focus();
							}, 100);
						}
						return;
					}

					// Store reference globally for merge tag insertion
					window.nfCurrentQuillInstance = quill;
					window.nfCurrentCodeMirror = null;

					// Mark the editor as focused
					jQuery(quill.root).addClass('merge-tag-focus');

					// Get cursor position - use last known position or end of content if no selection
					var range = quill.getSelection();
					var insertIndex;
					if (range) {
						insertIndex = range.index;
					} else {
						// No selection (editor lost focus), insert at end of content
						// getLength() includes the trailing newline, so subtract 1
						insertIndex = Math.max(0, quill.getLength() - 1);
					}

					// Mark as user-edited since clicking the merge tag button is an intentional edit.
					// Without this, the insertText call below fires text-change with source 'api',
					// which would not set _nfUserHasEdited and the merge tag would not be persisted.
					quill._nfUserHasEdited = true;

					// Insert a { character to trigger merge tag mode
					quill.insertText(insertIndex, '{');
					quill.setSelection(insertIndex + 1);

					// Set merge tag system variables
					nfRadio.channel('mergeTags').request('set:old', '{');
					nfRadio.channel('mergeTags').request('set:caret', insertIndex + 1);

					// Add overlay class
					var container = jQuery(quill.root).closest('.nf-setting');
					container.find('.ql-container').parent().addClass('merge-tag-focus-overlay');

					// Update tag list
					nfRadio.channel('merge-tags').request('update:taglist', 'fields', false);

					// Show the merge tags box
					jQuery('#merge-tags-box').css('display', 'block');

					// Position it (call focusCallback)
					nfRadio.channel('mergeTags').trigger('focus', quill.root, 'rte');

					// Focus the search input
					setTimeout(function() {
						jQuery('#merge-tags-box').find('.merge-tag-filter').find('input').focus();
					}, 100);
				});
			}, 100);

			// Handle content changes - persist when content has actually changed
			quill.on('text-change', function(delta, oldDelta, source) {
				// Skip the text-change fired by innerHTML assignment during initialization
				if (quill._nfSkipNextTextChange) {
					quill._nfSkipNextTextChange = false;
					return;
				}

				// Mark as edited if source is 'user' (direct user interaction)
				if (source === 'user') {
					quill._nfUserHasEdited = true;
				}

				// Also mark as edited if content has substantially changed from original,
				// even for API changes (e.g., dangerouslyPasteHTML). This ensures
				// programmatic content insertion is persisted to the data model.
				if (!quill._nfUserHasEdited) {
					var currentHtml = quill.root.innerHTML;
					var originalHtml = quill._nfOriginalHtml || '';
					// Compare content (normalize whitespace for comparison)
					var currentNormalized = currentHtml.replace(/\s+/g, ' ').trim();
					var originalNormalized = originalHtml.replace(/\s+/g, ' ').trim();
					if (currentNormalized !== originalNormalized) {
						quill._nfUserHasEdited = true;
					}
				}

				if (quill._nfUserHasEdited) {
					var value = quill.root.innerHTML;
					that.updateDataModel(settingModel, dataModel, value);
					nfRadio.channel( 'quill' ).trigger( 'change', settingModel, dataModel, value );
				}
			});

			// Handle focus and blur
			quill.on('selection-change', function(range, oldRange, source) {
				if (range) {
					// Mark the editor as focused for merge tags
					jQuery(quill.root).addClass('merge-tag-focus');
					// Set global reference for merge tag insertion
					window.nfCurrentQuillInstance = quill;
					nfRadio.channel( 'quill' ).trigger( 'focus', settingModel, dataModel, quill );
				} else if (oldRange) {
					jQuery(quill.root).removeClass('merge-tag-focus');
					if (quill._nfUserHasEdited) {
						var value = quill.root.innerHTML;
						that.updateDataModel(settingModel, dataModel, value);
						nfRadio.channel( 'quill' ).trigger( 'blur', settingModel, dataModel, value );
					} else {
						nfRadio.channel( 'quill' ).trigger( 'blur', settingModel, dataModel, quill._nfOriginalHtml );
					}
				}
			});

			// Clear global Quill reference on blur to prevent the global ESC
			// handler from using stale state to delete content (#7884).
			// Registered as a separate listener to avoid conflicts with the
			// blur logic in the handler above (which may be modified by #7865).
			quill.on('selection-change', function(range, oldRange, source) {
				if (!range && oldRange) {
					if ( 'none' === jQuery( '#merge-tags-box' ).css( 'display' ) ) {
						window.nfCurrentQuillInstance = null;
					}
				}
			});

			// Handle text changes for merge tag detection (typing '{')
			var mergeTagBoxLastShown = 0;
			quill.on('text-change', function(delta, oldDelta, source) {
				// Skip the text-change fired by initial innerHTML assignment.
				// Quill's MutationObserver reports DOM-level mutations as 'user'
				// even when triggered programmatically, so without this guard the
				// merge-tag box would auto-appear if loaded content contained
				// open-looking merge tag patterns (#7884).
				// Consume the flag BEFORE the source check so it can't get stuck
				// set if Quill ever fires the init event with a non-user source.
				if (quill._nfSkipNextMergeTagDetection) {
					quill._nfSkipNextMergeTagDetection = false;
					return;
				}

				// Only handle user input, not programmatic changes
				if (source !== 'user') return;

				// Bulk inserts (paste, dangerouslyPasteHTML, programmatic content
				// load) shouldn't trigger the merge-tag UI even if the inserted
				// content has a stray `{`. A real keystroke inserts exactly one
				// character per text-change.
				var totalInsertLength = 0;
				if (delta && delta.ops) {
					for (var i = 0; i < delta.ops.length; i++) {
						var op = delta.ops[i];
						if (op.insert && typeof op.insert === 'string') {
							totalInsertLength += op.insert.length;
						}
					}
				}
				var isBulkInsert = totalInsertLength > 1;

				// Get cursor position
				var range = quill.getSelection();
				var caretPos = range ? range.index : 0;
				nfRadio.channel('mergeTags').request('set:caret', caretPos);

				// Get plain text content (not HTML)
				var content = quill.getText();

				// Find open merge tags in the content
				var mergetags = content.match(/{([a-zA-Z0-9]|:|_|-|}| )*/g);

				// Filter out closed merge tags
				if (mergetags) {
					mergetags = _.filter(mergetags, function(mergetag) {
						return -1 == mergetag.indexOf('}');
					});
				}

				// If an open merge tag is found, show the Merge Tag Box —
				// but only when the change was a single-character edit (the
				// user typed `{` or deleted a `}`). Bulk inserts must not
				// auto-open the box (#7884).
				if (mergetags && mergetags.length > 0 && !isBulkInsert) {
					nfRadio.channel('mergeTags').request('set:old', mergetags[0]);

					jQuery('#merge-tags-box').css('display', 'block');
					nfRadio.channel('drawer').request('prevent:close');
					jQuery(quill.root).addClass('merge-tag-focus');

					// Store global reference
					window.nfCurrentQuillInstance = quill;

					// Add overlay class
					var container = jQuery(quill.root).closest('.nf-setting');
					container.find('.ql-container').parent().addClass('merge-tag-focus-overlay');

					// Position merge tag box
					nfRadio.channel('mergeTags').trigger('focus', quill.root, 'rte');

					// Filter the tag list
					var value = mergetags[0].replace('{', '');
					nfRadio.channel('merge-tags').request('filtertags', value);

					// Focus the search input (only if box wasn't already visible)
					if (!jQuery('#merge-tags-box').is(':visible') || (Date.now() - mergeTagBoxLastShown > 200)) {
						setTimeout(function() {
							jQuery('#merge-tags-box').find('.merge-tag-filter').find('input').focus();
						}, 100);
					}

					// Track when we showed the box
					mergeTagBoxLastShown = Date.now();
				} else {
					// Hide merge tag box if no open merge tags
					// But don't hide if we just showed it (within 200ms) to prevent flicker
					var timeSinceShown = Date.now() - mergeTagBoxLastShown;
					if (timeSinceShown > 200 && jQuery('#merge-tags-box').is(':visible')) {
						jQuery('#merge-tags-box').css('display', 'none');
						nfRadio.channel('drawer').request('enable:close');
						jQuery('#merge-tags-box').removeClass();
						jQuery('.merge-tag-focus-overlay').removeClass('merge-tag-focus-overlay');
					}
				}
			});

			// Handle right-click context menu for tables
			jQuery(quill.root).on('contextmenu', function(e) {
				that.showTableContextMenu(quill, e);
			});

			// Check for HTML degradation or unsupported HTML and auto-switch to code view if needed
			var quillHtml = quill.root.innerHTML;
			var hasUnsupported = quill._nfOriginalHtml && that.hasUnsupportedHtml(quill._nfOriginalHtml);
			// Compare NORMALIZED HTML to Quill output, not raw original (#7944)
			// Raw HTML may have malformed structures that get fixed during normalization,
			// so comparing raw to output would falsely trigger content loss detection
			var normalizedOriginal = quill._nfOriginalHtml ? that.normalizeTableHtml(quill._nfOriginalHtml) : '';
			var hasDegradation = normalizedOriginal && that.hasHtmlDegradation(normalizedOriginal, quillHtml);
			if (hasUnsupported || hasDegradation) {
				// Auto-switch to code view with original HTML preserved
				that.toggleCodeView(quill, settingView, settingModel, dataModel, quill._nfOriginalHtml);
				// Show notice to user
				that.showCodeViewNotice(settingView);
			}

			// Store reference for later
			settingEl.data('quill-instance', instanceId);
		},

		addDashicons: function(settingView, settingModel) {
			var toolbar = jQuery(settingView.el).find('.ql-toolbar');

			// Replace button content with dashicons
			toolbar.find('.ql-bold').html('<span class="dashicons dashicons-editor-bold"></span>');
			toolbar.find('.ql-italic').html('<span class="dashicons dashicons-editor-italic"></span>');
			toolbar.find('.ql-underline').html('<span class="dashicons dashicons-editor-underline"></span>');
			toolbar.find('.ql-list[value="ordered"]').html('<span class="dashicons dashicons-editor-ol"></span>');
			toolbar.find('.ql-list[value="bullet"]').html('<span class="dashicons dashicons-editor-ul"></span>');
			// Note: Don't replace .ql-align content - it's a dropdown picker that needs its internal structure
			toolbar.find('.ql-clean').html('<span class="dashicons dashicons-editor-removeformatting"></span>');

			// Add custom buttons with dashicons
			toolbar.find('button.ql-nf-link').html('<span class="dashicons dashicons-admin-links"></span>');
			toolbar.find('button.ql-nf-unlink').html('<span class="dashicons dashicons-editor-unlink"></span>');
			toolbar.find('button.ql-nf-media').html('<span class="dashicons dashicons-admin-media"></span>');

			// Find or create merge tags button
			var mergeButton = toolbar.find('button.ql-nf-merge-tags');
			if (mergeButton.length === 0) {
				// Quill didn't create the button, so create it manually in the last .ql-formats group
				var lastFormatsGroup = toolbar.find('.ql-formats').last();
				if (lastFormatsGroup.length === 0) {
					// Create a new formats group if none exists
					lastFormatsGroup = jQuery('<span class="ql-formats"></span>');
					toolbar.append(lastFormatsGroup);
				}
				mergeButton = jQuery('<button type="button" class="ql-nf-merge-tags merge-tags"><span class="dashicons dashicons-list-view"></span></button>');
				lastFormatsGroup.append(mergeButton);
			} else {
				mergeButton.html('<span class="dashicons dashicons-list-view"></span>').addClass('merge-tags');
			}

			// Add data-testid for E2E testing, matching pattern from other settings
			var settingName = settingModel ? settingModel.get('name') : '';
			if (settingName) {
				mergeButton.attr('data-testid', 'nf-merge-tag-picker-' + settingName);
			}

			toolbar.find('.ql-table').html('<span class="dashicons dashicons-editor-table"></span>');
			toolbar.find('button.ql-nf-code').html('<span class="dashicons dashicons-editor-code"></span>');
			toolbar.find('button.ql-nf-undo').html('<span class="dashicons dashicons-undo"></span>');
			toolbar.find('button.ql-nf-redo').html('<span class="dashicons dashicons-redo"></span>');
		},

		/**
		 * Sanitize HTML before saving to remove Quill artifacts and normalize lists.
		 *
		 * Removes:
		 * - <span class="ql-ui"></span> elements injected by Quill's list handling
		 * - Empty ql-* class attributes left after filtering
		 *
		 * Normalizes:
		 * - Quill's <ol><li data-list="bullet"> to standard <ul><li>
		 * - Quill's <ol><li data-list="ordered"> to standard <ol><li> (removes data-list)
		 *
		 * @param {string} html - The HTML to sanitize
		 * @returns {string} - Sanitized HTML
		 * @since 3.14.4
		 * @see https://github.com/Saturday-Drive/ninja-forms/issues/7937
		 * @see https://github.com/Saturday-Drive/ninja-forms/issues/7938
		 */
		sanitizeQuillHtml: function(html) {
			if (!html) return html;

			// Remove ql-ui spans that Quill injects for list item UI
			// These are empty spans used for checkbox toggle and shouldn't be persisted
			html = html.replace(/<span\s+class="ql-ui"[^>]*>\s*<\/span>/gi, '');

			// Remove any other empty ql-* spans
			html = html.replace(/<span\s+class="ql-[^"]*"[^>]*>\s*<\/span>/gi, '');

			// Clean up any data-list attributes with ql-ui contamination
			// This handles edge cases where ql-ui class got added alongside data-list
			html = html.replace(/\s+class="ql-ui"/gi, '');

			// Normalize Quill's list format back to standard HTML
			// Quill uses <ol> for ALL lists with data-list="bullet|ordered" on <li> elements
			html = this.normalizeQuillLists(html);

			return html;
		},

		/**
		 * Pre-process HTML before loading into Quill.
		 *
		 * Converts standard <ul> to Quill's internal format:
		 * - <ul><li>Item</li></ul> → <ol><li data-list="bullet">Item</li></ol>
		 *
		 * This allows Quill to track the list type so we can restore it on save.
		 *
		 * @param {string} html - The HTML to pre-process
		 * @returns {string} - HTML formatted for Quill consumption
		 * @since 3.14.4
		 * @see https://github.com/Saturday-Drive/ninja-forms/issues/7937
		 */
		prepareHtmlForQuill: function(html) {
			if (!html || html.indexOf('<ul') === -1) return html;

			// Use a temporary DOM element to parse and manipulate the HTML
			var temp = document.createElement('div');
			temp.innerHTML = html;

			// Find all <ul> elements and convert to <ol> with data-list="bullet"
			var bulletLists = temp.querySelectorAll('ul');

			bulletLists.forEach(function(ul) {
				// Create replacement <ol>
				var ol = document.createElement('ol');

				// Copy attributes from ul to ol
				for (var i = 0; i < ul.attributes.length; i++) {
					var attr = ul.attributes[i];
					ol.setAttribute(attr.name, attr.value);
				}

				// Mark all <li> children with data-list="bullet"
				var listItems = ul.querySelectorAll(':scope > li');
				listItems.forEach(function(li) {
					li.setAttribute('data-list', 'bullet');
				});

				// Move children to new ol
				while (ul.firstChild) {
					ol.appendChild(ul.firstChild);
				}

				// Replace ul with ol
				ul.parentNode.replaceChild(ol, ul);
			});

			return temp.innerHTML;
		},

		/**
		 * Convert Quill's list format back to standard HTML lists.
		 *
		 * Quill internally uses <ol> for all lists, differentiating by data-list attribute:
		 * - <ol><li data-list="bullet">Item</li></ol> → <ul><li>Item</li></ul>
		 * - <ol><li data-list="ordered">Item</li></ol> → <ol><li>Item</li></ol>
		 *
		 * @param {string} html - The HTML to normalize
		 * @returns {string} - Normalized HTML with proper <ul>/<ol> tags
		 * @since 3.14.4
		 * @see https://github.com/Saturday-Drive/ninja-forms/issues/7937
		 */
		normalizeQuillLists: function(html) {
			if (!html || html.indexOf('<ol') === -1) return html;

			// Use a temporary DOM element to parse and manipulate the HTML
			var temp = document.createElement('div');
			temp.innerHTML = html;

			// Find all <ol> elements
			var orderedLists = temp.querySelectorAll('ol');

			orderedLists.forEach(function(ol) {
				// Get direct <li> children only
				var listItems = ol.querySelectorAll(':scope > li');
				if (listItems.length === 0) return;

				// Check if all items are bullet type
				var allBullet = true;
				var hasBullet = false;

				listItems.forEach(function(li) {
					var dataList = li.getAttribute('data-list');
					if (dataList === 'bullet') {
						hasBullet = true;
					} else {
						allBullet = false;
					}
				});

				// If all items are bullet type, convert <ol> to <ul>
				if (allBullet && hasBullet) {
					var ul = document.createElement('ul');
					// Copy attributes from ol to ul (except type-specific ones)
					for (var i = 0; i < ol.attributes.length; i++) {
						var attr = ol.attributes[i];
						if (attr.name !== 'type' && attr.name !== 'start') {
							ul.setAttribute(attr.name, attr.value);
						}
					}
					// Move children to new ul
					while (ol.firstChild) {
						ul.appendChild(ol.firstChild);
					}
					ol.parentNode.replaceChild(ul, ol);
				}
			});

			// Clean up data-list attributes from all list items
			var allItems = temp.querySelectorAll('li[data-list]');
			allItems.forEach(function(li) {
				li.removeAttribute('data-list');
			});

			return temp.innerHTML;
		},

		updateDataModel: function( settingModel, dataModel, value ) {
			var name = settingModel.get( 'name' );
			var before = dataModel.get( name );
			// Sanitize HTML before saving to remove Quill artifacts
			var after = this.sanitizeQuillHtml(value);

			var changes = {
				attr: name,
				before: before,
				after: after
			}

			var label = {
				object: dataModel.get( 'objectType' ),
				label: dataModel.get( 'label' ),
				change: 'Changed ' + settingModel.get( 'label' ) + ' from ' + before + ' to ' + after
			};

			nfRadio.channel( 'changes' ).request( 'register:change', 'changeSetting', dataModel, changes, label );

			dataModel.set( settingModel.get( 'name' ), after );
		},

		renderSetting: function( settingModel, dataModel, settingView ) {
			this.initRTE( settingModel, dataModel, settingView );
		},

		destroySetting: function( settingModel, dataModel, settingView ) {
			this.removeRTE( settingModel, dataModel, settingView );
		},

		removeRTE: function( settingModel, dataModel, settingView ) {
			var settingEl = jQuery( settingView.el ).find( 'div.setting' );
			var instanceId = settingEl.data('quill-instance');

			if (instanceId && this.quillInstances[instanceId]) {
				delete this.quillInstances[instanceId];
			}

			settingEl.empty();
		},

		showLinkDialog: function(quill, settingView) {
			var that = this;
			var range = quill.getSelection(true);
			var text = range ? quill.getText(range.index, range.length) : '';

			// Create link dialog
			var linkDropdown = nfRadio.channel( 'app' ).request( 'get:template', '#tmpl-nf-rte-link-dropdown' );
			var dropdownHtml = linkDropdown({});

			var toolbar = jQuery(settingView.el).find('.ql-toolbar');
			var existingDropdown = toolbar.find('.nf-link-dropdown');

			if (existingDropdown.length) {
				existingDropdown.remove();
				return;
			}

			var dropdown = jQuery('<div class="nf-link-dropdown" role="dialog"></div>').attr('aria-label', nfi18n.insertLink || 'Insert Link').html(dropdownHtml);
			toolbar.append(dropdown);

			dropdown.find('.link-text').val(text);
			dropdown.find('.link-url').focus();

			// Close link dialog on Escape
			dropdown.on('keydown', function(ev) {
				if (ev.keyCode === 27) {
					ev.preventDefault();
					dropdown.remove();
					quill.focus();
				}
			});

			dropdown.find('.insert-link').on('click', function() {
				var linkText = dropdown.find('.link-text').val();
				var linkUrl = dropdown.find('.link-url').val();
				var newWindow = dropdown.find('.link-new-window').prop('checked');

				if (linkUrl) {
					// Mark as user-edited — inserting a link via the dialog is an intentional edit
					quill._nfUserHasEdited = true;

					var format = { link: linkUrl };
					if (newWindow) {
						format.target = '_blank';
					}

					if (range && range.length > 0) {
						quill.formatText(range.index, range.length, format);
					} else if (linkText) {
						quill.insertText(range.index, linkText, format);
						quill.setSelection(range.index + linkText.length);
					}
				}

				dropdown.remove();
			});

			dropdown.find('.cancel-link').on('click', function() {
				dropdown.remove();
			});

			// Store current quill instance for later
			that.currentContext = { quill: quill, range: range };
		},

		removeLink: function(quill) {
			var range = quill.getSelection();
			if (range) {
				// Mark as user-edited — removing a link via the toolbar is an intentional edit
				quill._nfUserHasEdited = true;
				quill.formatText(range.index, range.length, { link: false });
			}
		},

		openMediaManager: function(quill, settingView) {
			var that = this;
			var range = quill.getSelection(true);

			// Store context
			this.currentContext = { quill: quill, range: range };

			// If the frame already exists, re-open it.
			if ( this.meta_image_frame ) {
				this.meta_image_frame.open();
				return;
			}

			// Sets up the media library frame
			this.meta_image_frame = wp.media.frames.meta_image_frame = wp.media({
				title: 'Select a file',
				button: { text:  'insert' }
			});

			// Runs when an image is selected.
			this.meta_image_frame.on('select', function(){
				// Grabs the attachment selection and creates a JSON representation of the model.
				var media_attachment = that.meta_image_frame.state().get('selection').first().toJSON();
				that.insertMedia( media_attachment );
			});

			// Opens the media library frame.
			this.meta_image_frame.open();
		},

		insertMedia: function( media ) {
			var quill = this.currentContext.quill;
			var range = this.currentContext.range || quill.getSelection(true) || { index: 0 };

			// Mark as user-edited — inserting media via the library is an intentional edit
			quill._nfUserHasEdited = true;

			if ( 'image' == media.type ) {
				quill.insertEmbed(range.index, 'image', media.url);
				quill.setSelection(range.index + 1);
			} else {
				var linkText = media.title || media.filename;
				quill.insertText(range.index, linkText, { link: media.url });
				quill.setSelection(range.index + linkText.length);
			}
		},

		showMergeTags: function(quill, settingView) {
			// Mark the quill root as focused
			jQuery(quill.root).addClass('merge-tag-focus');

			// Store quill reference for merge tag insertion
			this.currentContext = { quill: quill };
		},

		hasHtmlDegradation: function(original, quillOutput) {
			// Quick empty check
			if (!original || original === '<p><br></p>') return false;

			// Normalize for comparison
			var normOrig = original.replace(/\s+/g, ' ').trim().toLowerCase();
			var normQuill = quillOutput.replace(/\s+/g, ' ').trim().toLowerCase();

			// If they're essentially the same, no degradation
			if (normOrig === normQuill) return false;

			// Check for lost elements that Quill doesn't support
			// Note: <div>, <span class=>, <span style=>, id=, style= are now supported
			// via custom blots and StyleAttributors (#7937, #7938, #7942)
			var unsupportedPatterns = [
				/<hr[\s/>]/i,           // horizontal rules
				/<thead[\s>]/i,         // table headers
				/<th[\s>]/i             // th elements
			];

			for (var i = 0; i < unsupportedPatterns.length; i++) {
				if (unsupportedPatterns[i].test(original) && !unsupportedPatterns[i].test(quillOutput)) {
					return true;
				}
			}

			// Check for significant content loss (>10% reduction)
			var origText = original.replace(/<[^>]+>/g, '').trim();
			var quillText = quillOutput.replace(/<[^>]+>/g, '').trim();
			if (origText.length > 0 && quillText.length < origText.length * 0.9) {
				return true;
			}

			return false;
		},

		/**
		 * Check if HTML contains features that Quill cannot preserve.
		 * Used to detect when content should be edited in code view instead of WYSIWYG.
		 *
		 * @param {string} html - The HTML to check
		 * @returns {boolean} - True if HTML has unsupported features
		 *
		 * @since 3.14.4 Removed patterns for:
		 *   - <ul> - Now supported via NfBulletList blot (#7937)
		 *   - <ol> - Now fixed, no ql-ui injection + sanitization (#7938)
		 *   - <span class=...> - Now supported via NfSpan blot (#7942)
		 *   - id attributes - Now supported via NfSpan blot (#7942)
		 */
		hasUnsupportedHtml: function(html) {
			if (!html) return false;

			// Patterns for HTML features Quill doesn't support
			// Note: <div> and style= are now supported via custom blots and StyleAttributors
			// Note: <ul>, <ol>, <span class=>, id= are now supported via custom blots (#7937, #7938, #7942)
			var unsupportedPatterns = [
				/<hr[\s/>]/i,           // horizontal rules - no Quill blot exists
				/<thead[\s>]/i,         // table headers - normalized to tbody
				/<th[\s>]/i             // th elements - normalized to td
			];

			for (var i = 0; i < unsupportedPatterns.length; i++) {
				if (unsupportedPatterns[i].test(html)) {
					return true;
				}
			}

			return false;
		},

		showCodeViewNotice: function(settingView) {
			var container = jQuery(settingView.el).find('.setting');
			var notice = jQuery('<div class="nf-code-view-notice" role="alert"></div>');
			notice.append(jQuery('<span></span>').text(nfi18n.quillCodeViewAutoSwitch));
			notice.append(jQuery('<button class="nf-dismiss-notice" type="button"></button>').attr('aria-label', nfi18n.dismiss || 'Dismiss').html('&times;'));
			notice.find('.nf-dismiss-notice').on('click', function() {
				notice.remove();
			});
			container.prepend(notice);
		},

		toggleCodeView: function(quill, settingView, settingModel, dataModel, htmlOverride) {
			var that = this;
			var container = jQuery(settingView.el).find('.setting');
			var toolbar = jQuery(settingView.el).find('.ql-toolbar');
			var isCodeView = container.hasClass('code-view');

			if (isCodeView) {
				// Switch back to WYSIWYG
				var codeMirror = container.data('codeMirror');
				var code = codeMirror ? codeMirror.getValue() : container.find('textarea').val();

				// Check if HTML has features Quill can't preserve (div, inline styles, etc.)
				// If so, stay in code view to prevent content loss.
				if (that.hasUnsupportedHtml(code)) {
					// Stay in code view — save to data model and notify user
					that.updateDataModel(settingModel, dataModel, code);
					that.showCodeViewNotice(settingView);
					return;
				}

				// Check for degradation via table normalization
				// Compare normalized HTML to what Quill would output, not raw to normalized
				// Raw HTML may have malformed structures that normalization intentionally fixes,
				// which is not degradation - it's repair (#7944)
				var normalized = that.normalizeTableHtml(code);
				// Only check for truly unsupported features, not structural repairs
				if (that.hasUnsupportedHtml(normalized)) {
					// Stay in code view — save to data model and notify user
					that.updateDataModel(settingModel, dataModel, code);
					that.showCodeViewNotice(settingView);
					return;
				}

				// Save raw code to data model BEFORE Quill normalizes it
				that.updateDataModel(settingModel, dataModel, code);
				// Reset state for the new content
				quill._nfOriginalHtml = code;
				quill._nfUserHasEdited = false;
				quill._nfSkipNextTextChange = true;
				// Sanitize table HTML to prevent infinite loop on malformed structures (#7944)
				// Then pre-process HTML to convert <ul> to Quill's internal format
				var processedHtml = that.normalizeTableHtml(code);
				processedHtml = that.prepareHtmlForQuill(processedHtml);
				quill.root.innerHTML = processedHtml;
				container.find('.code-view-wrapper').remove();
				container.find('.ql-container').show();
				toolbar.show();
				container.removeClass('code-view');
				container.removeData('codeMirror');

				// Remove active state from code button
				toolbar.find('.ql-nf-code').removeClass('ql-active');
			} else {
				// Switch to code view
				var html = htmlOverride || quill.root.innerHTML;

				// Sanitize HTML to remove Quill artifacts and restore proper list format
				var sanitizedHtml = that.sanitizeQuillHtml(html);

				// Save to data model immediately when switching to code view
				// This ensures the HTML is persisted even if the drawer closes before blur
				that.updateDataModel(settingModel, dataModel, sanitizedHtml);

				container.find('.ql-container').hide();

				// Keep toolbar visible but add active state to code button
				toolbar.find('.ql-nf-code').addClass('ql-active');

				// Create wrapper and textarea for CodeMirror
				var wrapper = jQuery('<div class="code-view-wrapper"></div>');
				var textarea = jQuery('<textarea class="code-view-textarea"></textarea>');
				textarea.val(sanitizedHtml);
				wrapper.append(textarea);
				container.append(wrapper);
				container.addClass('code-view');

				// Initialize CodeMirror with Monokai theme
				if (typeof CodeMirror !== 'undefined') {
					var codeMirror = CodeMirror.fromTextArea(textarea[0], {
						mode: 'xml',
						theme: 'monokai',
						lineNumbers: true,
						lineWrapping: true,
						viewportMargin: Infinity
					});
					codeMirror.setSize('100%', '200px');
					container.data('codeMirror', codeMirror);

					codeMirror.on('blur', function() {
						var value = codeMirror.getValue();
						that.updateDataModel(settingModel, dataModel, value);
					});
				} else {
					// Fallback if CodeMirror not loaded
					textarea.css({
						'width': '100%',
						'height': '150px',
						'font-family': 'monospace'
					});
					textarea.on('blur', function() {
						var value = jQuery(this).val();
						that.updateDataModel(settingModel, dataModel, value);
					});
				}
			}
		},

		hasLegacyTables: function(html) {
			if (html.toLowerCase().indexOf('<table') === -1) {
				return false;
			}
			var tmp = document.createElement('div');
			tmp.innerHTML = html;
			// Any <th> is inherently legacy — Quill only supports <td>
			if (tmp.querySelectorAll('th').length > 0) return true;
			var tds = tmp.querySelectorAll('td');
			for (var i = 0; i < tds.length; i++) {
				if (!tds[i].hasAttribute('data-row')) return true;
			}
			return false;
		},

		showLegacyTableNotice: function(settingView, instanceId, quill, settingModel, dataModel) {
			var that = this;
			var toolbar = jQuery(settingView.el).find('.ql-toolbar');
			var notice = jQuery('<div class="nf-legacy-table-notice" role="alert"></div>');
			notice.append(jQuery('<span class="nf-legacy-table-text"></span>').text(nfi18n.tableLegacyNotice));
			notice.append(jQuery('<button type="button" class="nf-convert-table-btn"></button>').text(nfi18n.tableLegacyConvert));
			notice.append(jQuery('<button type="button" class="nf-dismiss-notice"></button>').attr('aria-label', nfi18n.dismiss || 'Dismiss').html('&times;'));

			toolbar.after(notice);

			notice.find('.nf-convert-table-btn').on('click', function() {
				var value = quill.root.innerHTML;
				that.updateDataModel(settingModel, dataModel, value);
				nfRadio.channel('quill').trigger('change', settingModel, dataModel, value);
				notice.slideUp(function() { notice.remove(); });
			});

			notice.find('.nf-dismiss-notice').on('click', function() {
				notice.slideUp(function() { notice.remove(); });
			});
		},

		normalizeTableHtml: function(html) {
			if (html.toLowerCase().indexOf('<table') === -1) {
				return html;
			}

			var tmp = document.createElement('div');
			tmp.innerHTML = html;

			// === MALFORMED TABLE STRUCTURE FIXES (Issue #7944) ===
			// These fixes prevent Quill's infinite loop by ensuring proper table structure
			// before Quill's DOM parser processes the HTML.

			// 1. Remove text nodes (whitespace/newlines) from inside table structure elements
			// Whitespace between <table> and <td> creates text blots that cause checkMerge() to crash
			var tableElements = tmp.querySelectorAll('table, tbody, thead, tfoot, tr');
			for (var i = 0; i < tableElements.length; i++) {
				var el = tableElements[i];
				var childNodes = Array.prototype.slice.call(el.childNodes);
				for (var j = 0; j < childNodes.length; j++) {
					var child = childNodes[j];
					// Remove text nodes (nodeType 3) and comment nodes (nodeType 8)
					if (child.nodeType === 3 || child.nodeType === 8) {
						el.removeChild(child);
					}
				}
			}

			// 2. Fix orphan <td> elements that are direct children of <table> or <tbody>
			// Wrap them in <tr> elements
			var tables = tmp.querySelectorAll('table');
			for (var i = 0; i < tables.length; i++) {
				var table = tables[i];
				var tbody = table.querySelector('tbody');
				var container = tbody || table;

				var orphanTds = [];
				var children = Array.prototype.slice.call(container.children);
				for (var j = 0; j < children.length; j++) {
					if (children[j].tagName === 'TD' || children[j].tagName === 'TH') {
						orphanTds.push(children[j]);
					}
				}

				// Wrap consecutive orphan TDs in a single TR
				if (orphanTds.length > 0) {
					var tr = document.createElement('tr');
					var firstOrphan = orphanTds[0];
					container.insertBefore(tr, firstOrphan);
					for (var j = 0; j < orphanTds.length; j++) {
						tr.appendChild(orphanTds[j]);
					}
				}
			}

			// 3. Remove orphan <tr> elements that have no <td> children
			// These cause checkMerge() to fail when checking formats()
			var allTrs = tmp.querySelectorAll('tr');
			for (var i = 0; i < allTrs.length; i++) {
				var tr = allTrs[i];
				var hasCells = tr.querySelector('td, th');
				if (!hasCells) {
					tr.parentNode.removeChild(tr);
				}
			}

			// === END MALFORMED TABLE STRUCTURE FIXES ===

			// Convert <th> to <td> (Quill only supports TD), preserving all attributes
			var ths = tmp.querySelectorAll('th');
			for (var i = 0; i < ths.length; i++) {
				var td = document.createElement('td');
				td.innerHTML = ths[i].innerHTML;
				for (var a = 0; a < ths[i].attributes.length; a++) {
					td.setAttribute(ths[i].attributes[a].name, ths[i].attributes[a].value);
				}
				ths[i].parentNode.replaceChild(td, ths[i]);
			}

			// Move <thead> and <tfoot> rows into <tbody> (Quill only supports TBODY)
			var sections = tmp.querySelectorAll('thead, tfoot');
			for (var i = 0; i < sections.length; i++) {
				var table = sections[i].parentNode;
				var tbody = table.querySelector('tbody');
				if (!tbody) {
					tbody = document.createElement('tbody');
					table.appendChild(tbody);
				}
				var rows = sections[i].querySelectorAll('tr');
				var isThead = sections[i].tagName === 'THEAD';
				for (var j = 0; j < rows.length; j++) {
					if (isThead) {
						tbody.insertBefore(rows[j], tbody.firstChild);
					} else {
						tbody.appendChild(rows[j]);
					}
				}
				sections[i].parentNode.removeChild(sections[i]);
			}

			// Ensure every table has a <tbody> wrapper
			var tables = tmp.querySelectorAll('table');
			for (var i = 0; i < tables.length; i++) {
				if (!tables[i].querySelector('tbody')) {
					var tbody = document.createElement('tbody');
					var children = tables[i].children;
					var directTrs = [];
					for (var k = 0; k < children.length; k++) {
						if (children[k].tagName === 'TR') {
							directTrs.push(children[k]);
						}
					}
					for (var k = 0; k < directTrs.length; k++) {
						tbody.appendChild(directTrs[k]);
					}
					tables[i].appendChild(tbody);
				}
			}

			// Remove <caption>, <colgroup>, <col> (unsupported by Quill table blots)
			var unsupported = tmp.querySelectorAll('caption, colgroup, col');
			for (var i = 0; i < unsupported.length; i++) {
				unsupported[i].parentNode.removeChild(unsupported[i]);
			}

			// Add data-row to cells that don't have it (required by Quill table module)
			var trs = tmp.querySelectorAll('tr');
			for (var i = 0; i < trs.length; i++) {
				var cells = trs[i].querySelectorAll('td');
				var existingRowId = null;
				var needsId = false;

				for (var j = 0; j < cells.length; j++) {
					if (cells[j].hasAttribute('data-row')) {
						existingRowId = cells[j].getAttribute('data-row');
					} else {
						needsId = true;
					}
				}

				if (needsId) {
					var rowId = existingRowId || ('row-' + Math.random().toString(36).slice(2, 6));
					for (var j = 0; j < cells.length; j++) {
						if (!cells[j].hasAttribute('data-row')) {
							cells[j].setAttribute('data-row', rowId);
						}
					}
				}
			}

			return tmp.innerHTML;
		},

		showTablePicker: function(quill, settingView) {
			var range = quill.getSelection(true);
			var toolbar = jQuery(settingView.el).find('.ql-toolbar');

			// Close any existing picker (handles multi-instance case)
			var existingPicker = jQuery('.nf-table-picker');
			var wasOurs = toolbar.find('.nf-table-picker').length > 0;
			existingPicker.remove();
			jQuery(document).off('mousedown.nfTablePicker keydown.nfTablePicker');

			// Toggle behavior — clicking table button again closes its own picker
			if (wasOurs) {
				return;
			}

			// Build the 10x10 grid
			var maxRows = 10;
			var maxCols = 10;
			var picker = jQuery('<div class="nf-table-picker" role="dialog"></div>').attr('aria-label', nfi18n.tableInsertTable || 'Insert Table');
			var grid = jQuery('<div class="nf-table-grid" role="grid"></div>');
			var label = jQuery('<div class="nf-table-label" aria-live="polite">0 \u00d7 0</div>');

			var gridHtml = '';
			for (var r = 1; r <= maxRows; r++) {
				for (var c = 1; c <= maxCols; c++) {
					var isFirst = (r === 1 && c === 1);
					gridHtml += '<div class="nf-table-cell" role="gridcell" tabindex="' + (isFirst ? '0' : '-1') + '" data-row="' + r + '" data-col="' + c + '" aria-label="' + r + ' \u00d7 ' + c + '"></div>';
				}
			}
			grid.html(gridHtml);

			picker.append(grid);
			picker.append(label);
			toolbar.append(picker);

			// Focus the first cell for keyboard users
			grid.find('.nf-table-cell[data-row="1"][data-col="1"]').focus();

			// Cache grid cells for efficient hover highlighting
			var gridCells = grid[0].querySelectorAll('.nf-table-cell');

			// Highlight cells on hover
			grid.on('mouseover', '.nf-table-cell', function() {
				var row = parseInt(this.dataset.row, 10);
				var col = parseInt(this.dataset.col, 10);
				label.text(row + ' \u00d7 ' + col);

				for (var i = 0; i < gridCells.length; i++) {
					var r = parseInt(gridCells[i].dataset.row, 10);
					var c = parseInt(gridCells[i].dataset.col, 10);
					gridCells[i].classList.toggle('highlight', r <= row && c <= col);
				}
			});

			// Insert table on click
			grid.on('click', '.nf-table-cell', function() {
				var rows = parseInt(this.dataset.row, 10);
				var cols = parseInt(this.dataset.col, 10);

				quill.focus();
				if (range) {
					quill.setSelection(range.index, 0);
				}

				var tableModule = quill.getModule('table');
				if (tableModule) {
					quill._nfUserHasEdited = true;
					tableModule.insertTable(rows, cols);
				}

				picker.remove();
				jQuery(document).off('mousedown.nfTablePicker keydown.nfTablePicker');
			});

			// Keyboard navigation for grid
			grid.on('keydown', '.nf-table-cell', function(e) {
				var cell = jQuery(this);
				var row = parseInt(cell.data('row'), 10);
				var col = parseInt(cell.data('col'), 10);
				var targetRow = row;
				var targetCol = col;

				switch (e.keyCode) {
					case 37: targetCol = Math.max(1, col - 1); break; // Left
					case 38: targetRow = Math.max(1, row - 1); break; // Up
					case 39: targetCol = Math.min(maxCols, col + 1); break; // Right
					case 40: targetRow = Math.min(maxRows, row + 1); break; // Down
					case 13: // Enter — insert table
						cell.trigger('click');
						return;
					default:
						return; // Let other keys pass through
				}
				e.preventDefault();

				var target = grid.find('.nf-table-cell[data-row="' + targetRow + '"][data-col="' + targetCol + '"]');
				if (target.length) {
					cell.attr('tabindex', '-1');
					target.attr('tabindex', '0').focus();
					// Trigger highlight update
					target.trigger('mouseover');
				}
			});

			// Close on outside click
			setTimeout(function() {
				jQuery(document).on('mousedown.nfTablePicker', function(e) {
					if (!jQuery(e.target).closest('.nf-table-picker, .ql-table').length) {
						picker.remove();
						jQuery(document).off('mousedown.nfTablePicker keydown.nfTablePicker');
					}
				});
			}, 0);

			// Close on ESC
			jQuery(document).on('keydown.nfTablePicker', function(e) {
				if (e.keyCode === 27) {
					picker.remove();
					jQuery(document).off('mousedown.nfTablePicker keydown.nfTablePicker');
					quill.focus();
				}
			});
		},

		showTableContextMenu: function(quill, e) {
			// Remove any existing context menus and their event handlers
			jQuery('.nf-table-context-menu').remove();
			jQuery(document).off('mousedown.nfTableContext');

			// Check if right-click target is inside a table cell
			var td = jQuery(e.target).closest('td, th');
			if (!td.length) return;

			var tableModule = quill.getModule('table');
			if (!tableModule) return;

			e.preventDefault();

			var menuItems = [
				{ label: nfi18n.tableInsertRowAbove, action: 'insertRowAbove' },
				{ label: nfi18n.tableInsertRowBelow, action: 'insertRowBelow' },
				{ separator: true },
				{ label: nfi18n.tableInsertColumnLeft, action: 'insertColumnLeft' },
				{ label: nfi18n.tableInsertColumnRight, action: 'insertColumnRight' },
				{ separator: true },
				{ label: nfi18n.tableDeleteRow, action: 'deleteRow' },
				{ label: nfi18n.tableDeleteColumn, action: 'deleteColumn' },
				{ label: nfi18n.tableDeleteTable, action: 'deleteTable' }
			];

			var menu = jQuery('<div class="nf-table-context-menu" role="menu"></div>');

			menuItems.forEach(function(item, index) {
				if (item.separator) {
					menu.append(jQuery('<div class="nf-table-context-separator" role="separator"></div>'));
				} else {
					var menuItem = jQuery('<div class="nf-table-context-item" role="menuitem" tabindex="-1"></div>').text(item.label);
					menuItem.on('click', function() {
						quill._nfUserHasEdited = true;
						if (typeof tableModule[item.action] === 'function') {
							tableModule[item.action]();
						}
						menu.remove();
						jQuery(document).off('mousedown.nfTableContext keydown.nfTableContext');
						quill.focus();
					});
					menu.append(menuItem);
				}
			});

			// Position at mouse location
			menu.css({
				left: e.clientX + 'px',
				top: e.clientY + 'px'
			});

			jQuery('body').append(menu);

			// Focus first menu item for keyboard users
			menu.find('.nf-table-context-item').first().focus();

			// Keyboard navigation for context menu
			jQuery(document).on('keydown.nfTableContext', function(ev) {
				var items = menu.find('.nf-table-context-item');
				var focused = menu.find('.nf-table-context-item:focus');
				var idx = items.index(focused);

				switch (ev.keyCode) {
					case 40: // Down arrow
						ev.preventDefault();
						idx = (idx + 1) % items.length;
						items.eq(idx).focus();
						break;
					case 38: // Up arrow
						ev.preventDefault();
						idx = (idx - 1 + items.length) % items.length;
						items.eq(idx).focus();
						break;
					case 13: // Enter
						ev.preventDefault();
						if (focused.length) {
							focused.trigger('click');
						}
						break;
					case 27: // Escape
						ev.preventDefault();
						menu.remove();
						jQuery(document).off('mousedown.nfTableContext keydown.nfTableContext');
						quill.focus();
						break;
				}
			});

			// Close on outside click
			setTimeout(function() {
				jQuery(document).on('mousedown.nfTableContext', function(ev) {
					if (!jQuery(ev.target).closest('.nf-table-context-menu').length) {
						menu.remove();
						jQuery(document).off('mousedown.nfTableContext keydown.nfTableContext');
					}
				});
			}, 0);
		},

		clickExtra: function( e, settingModel, dataModel, settingView ) {
			// Handle any extra click events (like the insert link button)
			var textEl = jQuery( e.target ).parent().find( '.link-text' );
			var urlEl = jQuery( e.target ).parent().find( '.link-url' );
			var isNewWindowEl = jQuery( e.target ).parent().find( '.link-new-window' );

			if ( jQuery( e.target ).hasClass( 'insert-link' ) ) {
				var text = textEl.val();
				var url = urlEl.val();
				var isNewWindow = ( isNewWindowEl.prop( 'checked' ) ) ? true: false;

				if ( 0 != text.length && 0 != url.length && this.currentContext.quill ) {
					var quill = this.currentContext.quill;
					var range = this.currentContext.range || quill.getSelection(true) || { index: 0 };

					// Mark as user-edited — inserting a link via clickExtra is an intentional edit
					quill._nfUserHasEdited = true;

					var format = { link: url };
					if (isNewWindow) {
						format.target = '_blank';
					}

					quill.insertText(range.index, text, format);
					quill.setSelection(range.index + text.length);
				}
			}

			textEl.val( '' );
			urlEl.val( '' );
			isNewWindowEl.prop( 'checked', false );
			jQuery( e.target ).closest( '.nf-link-dropdown' ).remove();
		}
	});

	return controller;
} );

